import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    IconButton,
    Collapse,
    List,
    ListItem,
    ListItemText,
    Chip
} from '@mui/material';
import { ExpandMore, ExpandLess, Assignment, ArrowBack } from '@mui/icons-material';
import dayjs from 'dayjs';
import { t } from 'i18next';

export default function PatientPlans({ plans = [], onSelectPlan }) {
    const [expanded, setExpanded] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    const handleSelectPlan = (plan) => {
        if (plan) {
            setSelectedPlan(plan);
            setExpanded(false);
            onSelectPlan?.(plan);
        } else {
            setSelectedPlan(null);
            setExpanded(false);
            onSelectPlan?.(null);
        }
    };

    const isDefault = !selectedPlan;

    return (
        <Card
            elevation={3}
            sx={{
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: '#f9f9f9'
            }}
        >
            <CardContent
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    py: 3.5,
                    px: 2
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Assignment sx={{ fontSize: 44, color: '#1976d2', mr: 2 }} />
                    <Box>
                        {isDefault ? (
                            <>
                                <Typography variant="h5" fontWeight={600}>
                                    {plans.length}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {plans.length === 1
                                        ? t('plans.singlePlan')
                                        : t('plans.multiplePlans')}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="h6" fontWeight={600}>
                                    {selectedPlan.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {dayjs(selectedPlan.start_date).format('DD/MM/YYYY')}{' '}
                                    – {dayjs(selectedPlan.end_date).format('DD/MM/YYYY')}
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>
                <IconButton>{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
            </CardContent>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ borderTop: '1px solid #eee', px: 2, pb: 2 }}>
                    {plans.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {t('plans.noPlans')}
                        </Typography>
                    ) : (
                        <List dense>
                            <ListItem
                                onClick={() => handleSelectPlan(null)}
                                sx={{
                                    borderRadius: 2,
                                    mb: 0.5,
                                    cursor: 'pointer',
                                    backgroundColor: isDefault
                                        ? 'rgba(25,118,210,0.08)'
                                        : 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'rgba(25,118,210,0.08)'
                                    }
                                }}
                            >
                                <ArrowBack sx={{ mr: 1, color: '#1976d2' }} />
                                <ListItemText
                                    primary={t('plans.allPlans')}
                                    secondary={t('plans.totalPlans', {
                                        count: plans.length
                                    })}
                                />
                            </ListItem>

                            {plans.map((plan) => (
                                <ListItem
                                    key={plan.uuid}
                                    onClick={() => handleSelectPlan(plan)}
                                    sx={{
                                        borderRadius: 2,
                                        mb: 0.5,
                                        cursor: 'pointer',
                                        backgroundColor:
                                            selectedPlan?.uuid === plan.uuid
                                                ? 'rgba(25,118,210,0.08)'
                                                : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'rgba(25,118,210,0.08)'
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary={
                                            <Typography variant="body1" fontWeight={500}>
                                                {plan.name}
                                            </Typography>
                                        }
                                        secondary={`${dayjs(plan.start_date).format('DD/MM/YYYY')} – ${dayjs(plan.end_date).format('DD/MM/YYYY')}`}
                                    />
                                    <Chip
                                        label={
                                            plan.accepted
                                                ? t('plans.accepted')
                                                : t('plans.pending')
                                        }
                                        color={plan.accepted ? 'success' : 'warning'}
                                        size="small"
                                        sx={{ ml: 1 }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </Collapse>
        </Card>
    );
}
